/** Official exams (B11) in the portal: exam types (administration), and a student's sittings. */
import { useState, type FormEvent } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import type {
  Exam,
  ExamKind,
  ExaminersResponse,
  ExamsResponse,
  ExamStatus,
  ExamType,
  ExamTypesResponse,
  SurahsResponse,
} from "@mubin/contracts";
import { api, errorText, send } from "../lib/api";
import { Badge, Empty, ErrorNote, Field, Loading, Modal, useSubmit } from "./ui";

export const examKindLabel: Record<ExamKind, string> = {
  juz_end: "نهاية جزء",
  multi_juz: "كل عدد من الأجزاء",
  periodic: "دوري",
  final: "ختامي",
  ijaza: "إجازة",
  other: "أخرى",
};
export const examStatusLabel: Record<ExamStatus, string> = {
  scheduled: "مجدول",
  recorded: "بانتظار الاعتماد",
  approved: "معتمد",
  cancelled: "ملغى",
};
const statusTone = {
  scheduled: "info",
  recorded: "warning",
  approved: "success",
  cancelled: "neutral",
} as const;
const approverLabel = { supervisor: "المشرف فأعلى", org_admin: "إدارة الجمعية" } as const;

// ------------------------------------------------------------------ exam types (administration)

export function ExamTypesCard({ organizationId }: { organizationId: string }) {
  const types = useQuery({
    queryKey: ["exam-types", organizationId],
    queryFn: () => api<ExamTypesResponse>(`/v1/exam-types?organizationId=${organizationId}`),
  });
  const [editing, setEditing] = useState<ExamType | "new" | null>(null);
  return (
    <section className="card">
      <div className="card-h">
        <h2>أنواع الاختبارات</h2>
        <button className="btn btn-secondary btn-sm" onClick={() => setEditing("new")}>
          نوع جديد
        </button>
      </div>
      {types.isPending ? (
        <Loading rows={2} />
      ) : !types.data?.examTypes.length ? (
        <Empty title="لا أنواع اختبارات بعد">
          <p className="muted small">
            حدّد كل نوع: درجته العظمى (10 أو 30 أو 50 أو 100 أو غيرها)، ودرجة النجاح، وعدد المحاولات، ومن
            يعتمد نتيجته.
          </p>
        </Empty>
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>النوع</th>
                <th>الدرجة</th>
                <th>المحاولات</th>
                <th>الاعتماد</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {types.data.examTypes.map((t) => (
                <tr key={t.id}>
                  <td>
                    <b>{t.name}</b> <span className="muted small">· {examKindLabel[t.kind]}</span>{" "}
                    {t.archived && <Badge tone="neutral">مؤرشف</Badge>}
                  </td>
                  <td className="num">
                    النجاح {t.passScore} من {t.maxScore}
                  </td>
                  <td className="num">{t.maxAttempts}</td>
                  <td className="small">{approverLabel[t.approverRole]}</td>
                  <td>
                    <button className="btn btn-ghost btn-sm" onClick={() => setEditing(t)}>
                      تعديل
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <ExamTypeModal organizationId={organizationId} editing={editing} onClose={() => setEditing(null)} />
    </section>
  );
}

function ExamTypeModal({
  organizationId,
  editing,
  onClose,
}: {
  organizationId: string;
  editing: ExamType | "new" | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const { busy, error, run } = useSubmit();
  const current = editing && editing !== "new" ? editing : null;
  const [form, setForm] = useState({
    name: "",
    kind: "juz_end" as ExamKind,
    maxScore: 100,
    passScore: 60,
    maxAttempts: 2,
    approverRole: "supervisor" as "supervisor" | "org_admin",
    archived: false,
  });
  const [loadedFor, setLoadedFor] = useState<string | null>(null);
  const key = current?.id ?? (editing === "new" ? "new" : null);
  if (key !== loadedFor) {
    setLoadedFor(key);
    setForm(
      current
        ? { ...current }
        : {
            name: "",
            kind: "juz_end",
            maxScore: 100,
            passScore: 60,
            maxAttempts: 2,
            approverRole: "supervisor",
            archived: false,
          },
    );
  }
  function submit(e: FormEvent) {
    e.preventDefault();
    void run(async () => {
      const { archived, ...rest } = form;
      if (current) {
        await api(`/v1/exam-types/${current.id}`, {
          method: "PUT",
          json: { organizationId, ...rest, archived },
        });
      } else {
        await send("POST", "/v1/exam-types", { organizationId, ...rest });
      }
      await qc.invalidateQueries({ queryKey: ["exam-types"] });
      onClose();
    }, errorText);
  }
  return (
    <Modal title={current ? "تعديل نوع الاختبار" : "نوع اختبار جديد"} open={!!editing} onClose={onClose}>
      <form className="form" onSubmit={submit}>
        <ErrorNote error={error} />
        <Field id="et-name" label="الاسم">
          <input
            id="et-name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            required
          />
        </Field>
        <Field id="et-kind" label="الصنف">
          <select
            id="et-kind"
            value={form.kind}
            onChange={(e) => setForm({ ...form, kind: e.target.value as ExamKind })}
          >
            {Object.entries(examKindLabel).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
        </Field>
        <div className="form-row">
          <Field id="et-max" label="الدرجة العظمى" hint="مثل 10 أو 30 أو 50 أو 100">
            <input
              id="et-max"
              type="number"
              dir="ltr"
              min={1}
              step={1}
              list="et-max-presets"
              value={form.maxScore}
              onChange={(e) => setForm({ ...form, maxScore: Number(e.target.value) })}
            />
            <datalist id="et-max-presets">
              {[10, 30, 50, 100].map((n) => (
                <option key={n} value={n} />
              ))}
            </datalist>
          </Field>
          <Field id="et-pass" label="درجة النجاح">
            <input
              id="et-pass"
              type="number"
              dir="ltr"
              min={0}
              step={0.5}
              value={form.passScore}
              onChange={(e) => setForm({ ...form, passScore: Number(e.target.value) })}
            />
          </Field>
        </div>
        <div className="form-row">
          <Field id="et-att" label="عدد المحاولات المسموحة">
            <input
              id="et-att"
              type="number"
              dir="ltr"
              min={1}
              max={20}
              value={form.maxAttempts}
              onChange={(e) => setForm({ ...form, maxAttempts: Number(e.target.value) })}
            />
          </Field>
          <Field id="et-appr" label="يعتمد النتيجة">
            <select
              id="et-appr"
              value={form.approverRole}
              onChange={(e) =>
                setForm({ ...form, approverRole: e.target.value as "supervisor" | "org_admin" })
              }
            >
              <option value="supervisor">المشرف (ومن فوقه)</option>
              <option value="org_admin">إدارة الجمعية فقط</option>
            </select>
          </Field>
        </div>
        {current && (
          <label className="check">
            <input
              type="checkbox"
              checked={form.archived}
              onChange={(e) => setForm({ ...form, archived: e.target.checked })}
            />
            مؤرشف: لا يُجدول به اختبار جديد (الاختبارات السابقة تبقى)
          </label>
        )}
        <p className="muted small">
          التعديل لا يغيّر الاختبارات المجدولة سابقًا؛ كل اختبار يحتفظ بقواعده وقت جدولته.
        </p>
        <div className="form-actions">
          <button className="btn btn-primary" disabled={busy}>
            {busy ? "جارٍ الحفظ…" : "حفظ"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ------------------------------------------------------------------ a student's sittings

export function StudentExams({
  organizationId,
  enrollmentId,
  canSchedule,
}: {
  organizationId: string;
  enrollmentId: string;
  canSchedule: boolean;
}) {
  return (
    <ExamsCard
      query={`enrollmentId=${enrollmentId}`}
      schedule={canSchedule ? { organizationId, enrollmentId } : undefined}
    />
  );
}

/**
 * Sittings matching a query, with the actions the server allows on each (record, approve, cancel).
 * Used on a student's page and on the exams page (all sittings in the caller's scope).
 */
export function ExamsCard({
  query,
  title = "الاختبارات",
  showStudent = false,
  schedule,
  empty = "لا اختبارات بعد",
}: {
  query: string;
  title?: string;
  showStudent?: boolean;
  schedule?: { organizationId: string; enrollmentId: string };
  empty?: string;
}) {
  const exams = useQuery({
    queryKey: ["exams", query],
    queryFn: () => api<ExamsResponse>(`/v1/exams?${query}`),
  });
  const [modal, setModal] = useState<{ kind: "schedule" } | { kind: "record"; exam: Exam } | null>(null);
  const qc = useQueryClient();
  const act = useSubmit();
  const doAction = (exam: Exam, action: "approve" | "cancel") =>
    void act.run(async () => {
      await send("POST", `/v1/exams/${exam.id}/${action}`, {});
      await qc.invalidateQueries({ queryKey: ["exams"] });
    }, errorText);

  return (
    <section className="card">
      <div className="card-h">
        <h2>{title}</h2>
        {schedule && (
          <button className="btn btn-secondary btn-sm" onClick={() => setModal({ kind: "schedule" })}>
            جدولة اختبار
          </button>
        )}
      </div>
      <ErrorNote error={act.error} />
      {exams.isPending ? (
        <Loading rows={2} />
      ) : !exams.data?.exams.length ? (
        <Empty title={empty} />
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                {showStudent && <th>الطالب</th>}
                <th>الاختبار</th>
                <th>المقطع</th>
                <th>الموعد والمختبِر</th>
                <th>النتيجة</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {exams.data.exams.map((x) => (
                <tr key={x.id}>
                  {showStudent && (
                    <td>
                      <Link to="/users/$userId" params={{ userId: x.student.id }}>
                        <b>{x.student.name}</b>
                      </Link>
                      <div className="muted small">{x.circle.name}</div>
                    </td>
                  )}
                  <td>
                    <b>{x.type.name}</b>
                    <div className="muted small">
                      المحاولة {x.attempt} من {x.maxAttempts}
                    </div>
                  </td>
                  <td className="small">{x.range}</td>
                  <td className="small">
                    <span className="num">{x.scheduledOn}</span> · {x.examiner.name}
                  </td>
                  <td>
                    <Badge tone={statusTone[x.status]}>{examStatusLabel[x.status]}</Badge>{" "}
                    {x.score !== null && (
                      <span className="num">
                        {x.score} / {x.maxScore} {x.passed ? "· ناجح" : "· لم ينجح"}
                      </span>
                    )}
                  </td>
                  <td className="actions">
                    {x.can.record && (
                      <button
                        className="btn btn-ghost btn-sm"
                        onClick={() => setModal({ kind: "record", exam: x })}
                      >
                        {x.status === "recorded" ? "تعديل الدرجة" : "تسجيل الدرجة"}
                      </button>
                    )}
                    {x.can.approve && (
                      <button
                        className="btn btn-secondary btn-sm"
                        disabled={act.busy}
                        onClick={() => doAction(x, "approve")}
                      >
                        اعتماد
                      </button>
                    )}
                    {x.can.cancel && (
                      <button
                        className="btn btn-ghost btn-sm"
                        disabled={act.busy}
                        onClick={() => doAction(x, "cancel")}
                      >
                        إلغاء
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {schedule && (
        <ScheduleModal
          open={modal?.kind === "schedule"}
          onClose={() => setModal(null)}
          organizationId={schedule.organizationId}
          enrollmentId={schedule.enrollmentId}
        />
      )}
      <RecordModal exam={modal?.kind === "record" ? modal.exam : null} onClose={() => setModal(null)} />
    </section>
  );
}

function ScheduleModal({
  open,
  onClose,
  organizationId,
  enrollmentId,
}: {
  open: boolean;
  onClose: () => void;
  organizationId: string;
  enrollmentId: string;
}) {
  const qc = useQueryClient();
  const { busy, error, run } = useSubmit();
  const types = useQuery({
    queryKey: ["exam-types", organizationId],
    queryFn: () => api<ExamTypesResponse>(`/v1/exam-types?organizationId=${organizationId}`),
    enabled: open,
  });
  const examiners = useQuery({
    queryKey: ["examiners", enrollmentId],
    queryFn: () => api<ExaminersResponse>(`/v1/exams/examiners?enrollmentId=${enrollmentId}`),
    enabled: open,
  });
  const surahs = useQuery({
    queryKey: ["surahs"],
    queryFn: () => api<SurahsResponse>("/v1/quran/surahs"),
    enabled: open,
    staleTime: Infinity,
  });
  const active = types.data?.examTypes.filter((t) => !t.archived) ?? [];
  const [form, setForm] = useState({
    examTypeId: "",
    startSurah: 78,
    startAyah: 1,
    endSurah: 114,
    endAyah: 6,
    scheduledOn: new Date().toISOString().slice(0, 10),
    examinerId: "",
  });
  const list = surahs.data?.surahs ?? [];
  const ayahsOf = (s: number) => list.find((x) => x.id === s)?.ayahCount ?? 286;
  const roleLabel: Record<string, string> = {
    teacher: "معلم",
    supervisor: "مشرف",
    branch_manager: "مدير مجمع",
    org_admin: "إدارة الجمعية",
  };

  function submit(e: FormEvent) {
    e.preventDefault();
    void run(async () => {
      await send("POST", "/v1/exams", {
        enrollmentId,
        examTypeId: form.examTypeId || active[0]?.id,
        start: { surah: form.startSurah, ayah: form.startAyah },
        end: { surah: form.endSurah, ayah: form.endAyah },
        scheduledOn: form.scheduledOn,
        examinerId: form.examinerId || examiners.data?.examiners[0]?.id,
      });
      await qc.invalidateQueries({ queryKey: ["exams"] });
      onClose();
    }, errorText);
  }

  return (
    <Modal title="جدولة اختبار" open={open} onClose={onClose}>
      {!active.length && !types.isPending ? (
        <p className="muted">لا أنواع اختبارات بعد. تُضاف من «الإعدادات» بحساب إدارة الجمعية.</p>
      ) : (
        <form className="form" onSubmit={submit}>
          <ErrorNote error={error} />
          <Field id="ex-type" label="نوع الاختبار">
            <select
              id="ex-type"
              value={form.examTypeId}
              onChange={(e) => setForm({ ...form, examTypeId: e.target.value })}
            >
              {active.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name} (النجاح {t.passScore} من {t.maxScore}، {t.maxAttempts} محاولات)
                </option>
              ))}
            </select>
          </Field>
          <div className="form-row">
            <Field id="ex-ss" label="من سورة">
              <select
                id="ex-ss"
                value={form.startSurah}
                onChange={(e) => setForm({ ...form, startSurah: Number(e.target.value), startAyah: 1 })}
              >
                {list.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.id}. {s.nameAr}
                  </option>
                ))}
              </select>
            </Field>
            <Field id="ex-sa" label={`آية (1–${ayahsOf(form.startSurah)})`}>
              <input
                id="ex-sa"
                type="number"
                dir="ltr"
                min={1}
                max={ayahsOf(form.startSurah)}
                value={form.startAyah}
                onChange={(e) => setForm({ ...form, startAyah: Number(e.target.value) })}
              />
            </Field>
          </div>
          <div className="form-row">
            <Field id="ex-es" label="إلى سورة">
              <select
                id="ex-es"
                value={form.endSurah}
                onChange={(e) => {
                  const s = Number(e.target.value);
                  setForm({ ...form, endSurah: s, endAyah: ayahsOf(s) });
                }}
              >
                {list.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.id}. {s.nameAr}
                  </option>
                ))}
              </select>
            </Field>
            <Field id="ex-ea" label={`آية (1–${ayahsOf(form.endSurah)})`}>
              <input
                id="ex-ea"
                type="number"
                dir="ltr"
                min={1}
                max={ayahsOf(form.endSurah)}
                value={form.endAyah}
                onChange={(e) => setForm({ ...form, endAyah: Number(e.target.value) })}
              />
            </Field>
          </div>
          <div className="form-row">
            <Field id="ex-date" label="الموعد">
              <input
                id="ex-date"
                type="date"
                dir="ltr"
                value={form.scheduledOn}
                onChange={(e) => setForm({ ...form, scheduledOn: e.target.value })}
              />
            </Field>
            <Field id="ex-examiner" label="المختبِر">
              <select
                id="ex-examiner"
                value={form.examinerId}
                onChange={(e) => setForm({ ...form, examinerId: e.target.value })}
              >
                {examiners.data?.examiners.map((x) => (
                  <option key={x.id} value={x.id}>
                    {x.name} · {roleLabel[x.role] ?? x.role}
                  </option>
                ))}
              </select>
            </Field>
          </div>
          <div className="form-actions">
            <button className="btn btn-primary" disabled={busy}>
              {busy ? "جارٍ الجدولة…" : "جدولة"}
            </button>
          </div>
        </form>
      )}
    </Modal>
  );
}

function RecordModal({ exam, onClose }: { exam: Exam | null; onClose: () => void }) {
  const qc = useQueryClient();
  const { busy, error, run } = useSubmit();
  const [score, setScore] = useState("");
  const [studentNote, setStudentNote] = useState("");
  const [examinerNote, setExaminerNote] = useState("");
  const [loadedFor, setLoadedFor] = useState<string | null>(null);
  if ((exam?.id ?? null) !== loadedFor) {
    setLoadedFor(exam?.id ?? null);
    setScore(exam?.score != null ? String(exam.score) : "");
    setStudentNote(exam?.studentNote ?? "");
    setExaminerNote(exam?.examinerNote ?? "");
  }
  function submit(e: FormEvent) {
    e.preventDefault();
    void run(async () => {
      await send("POST", `/v1/exams/${exam!.id}/record`, {
        score: Number(score),
        ...(studentNote.trim() ? { studentNote: studentNote.trim() } : {}),
        ...(examinerNote.trim() ? { examinerNote: examinerNote.trim() } : {}),
      });
      await qc.invalidateQueries({ queryKey: ["exams"] });
      onClose();
    }, errorText);
  }
  return (
    <Modal
      title={exam ? `درجة ${exam.student.name} · ${exam.type.name}` : ""}
      open={!!exam}
      onClose={onClose}
    >
      {exam && (
        <form className="form" onSubmit={submit}>
          <ErrorNote error={error} />
          <p className="muted small">
            {exam.range} · النجاح من {exam.passScore} · بعد التسجيل تنتظر النتيجة اعتماد{" "}
            {approverLabel[exam.approverRole]}
          </p>
          <Field id="rec-score" label={`الدرجة (من ${exam.maxScore})`}>
            <input
              id="rec-score"
              type="number"
              dir="ltr"
              min={0}
              max={exam.maxScore}
              step={0.5}
              value={score}
              onChange={(e) => setScore(e.target.value)}
              required
            />
          </Field>
          <Field id="rec-sn" label="ملاحظة للطالب" hint="تظهر له بعد الاعتماد">
            <textarea
              id="rec-sn"
              value={studentNote}
              onChange={(e) => setStudentNote(e.target.value)}
              rows={2}
            />
          </Field>
          <Field id="rec-en" label="ملاحظة داخلية" hint="للمعلمين والمشرفين فقط">
            <textarea
              id="rec-en"
              value={examinerNote}
              onChange={(e) => setExaminerNote(e.target.value)}
              rows={2}
            />
          </Field>
          <div className="form-actions">
            <button className="btn btn-primary" disabled={busy || score === ""}>
              {busy ? "جارٍ الحفظ…" : "حفظ الدرجة"}
            </button>
          </div>
        </form>
      )}
    </Modal>
  );
}
